// Scope 2 (electricity) v1-vs-v2 helpers.
//
// Scope 2 has two parallel calc engines. A questionnaire is owned by exactly one
// engine (guarded server-side), but that ownership is per-QUESTIONNAIRE, not
// per-period — so a single year/quarter can contain both v1- and v2-owned
// questionnaires. When it does, the Consumption tab and the Scope 2 drill-down
// show the v2 electricity breakdown and HIDE the v1 electricity (the "v2 wins"
// rule). The headline tCO2e totals still include both (summed server-side); only
// the kWh cards + per-category breakdown are v2-only. These helpers detect that
// mixed state so the UI can warn the user that some electricity is not shown.
//
// - v1 electricity rows come from `scopeOneAndTwoDetail.electricity` (or the
//   consumption payload's `energy.electricity`): objects with a backend
//   `consumptionType` (ElectricityType enum name) and native `consumption`.
// - v2 rows come from `electricityV2`: objects with a `category`
//   (PurchasedElectricityCategory) and `consumptionKwh`.

// v1 ElectricityType that is transport (EV mileage), not purchased electricity —
// its presence alone must NOT count as v1 electricity data.
const V1_ELECTRIC_CAR_PREFIX = "ELECTRIC_CAR";
// v1 market-based rows are the same physical kWh as location-based, so they are
// excluded from any kWh sum to avoid double-counting.
const V1_MARKET_BASED = "ELECTRICITY_MARKET_BASED";
// v2 on-site generation summary row — always present for v2 orgs and never a
// purchased category, so excluded from the "v2 present" test.
const V2_ONSITE = "ONSITE";

const isElectricCar = (type) => String(type || "").toUpperCase().startsWith(V1_ELECTRIC_CAR_PREFIX);

/**
 * True when the period carries at least one v1 PURCHASED electricity row with
 * positive consumption. EV (`ELECTRIC_CAR*`) rows are excluded — they are
 * transport, not purchased electricity.
 *
 * @param {Array<{consumptionType?: string, consumption?: number}>} electricityRows
 */
export function hasV1PurchasedElectricity(electricityRows) {
  if (!Array.isArray(electricityRows)) return false;
  return electricityRows.some(
    (r) => r && !isElectricCar(r.consumptionType) && (Number(r.consumption) || 0) > 0
  );
}

/**
 * True when the period carries at least one v2 purchased-electricity row with
 * positive kWh. The `ONSITE` summary row is excluded — it is always present for
 * a v2 org and is not a purchased category.
 *
 * @param {Array<{category?: string, consumptionKwh?: number}>} v2Rows
 */
export function hasV2PurchasedElectricity(v2Rows) {
  if (!Array.isArray(v2Rows)) return false;
  return v2Rows.some(
    (c) => c && c.category !== V2_ONSITE && (Number(c.consumptionKwh) || 0) > 0
  );
}

/**
 * True when the (already dimension/quarter-filtered) period contains BOTH v1 and
 * v2 purchased electricity — i.e. the "v2 wins" rule is hiding real v1 kWh.
 */
export function isMixedScopeTwoElectricity(electricityRows, v2Rows) {
  return hasV1PurchasedElectricity(electricityRows) && hasV2PurchasedElectricity(v2Rows);
}

/**
 * Sum of v1 purchased-electricity kWh that the "v2 wins" rule hides in a mixed
 * period — used to quantify the warning. Excludes EV (transport) and market-based
 * (same physical kWh as location-based) rows. v1 electricity is always in kWh, so
 * `consumption` is the kWh value.
 *
 * @param {Array<{consumptionType?: string, consumption?: number}>} electricityRows
 */
export function v1PurchasedElectricityKwh(electricityRows) {
  if (!Array.isArray(electricityRows)) return 0;
  return electricityRows
    .filter((r) => r && !isElectricCar(r.consumptionType))
    .filter((r) => String(r.consumptionType || "").toUpperCase() !== V1_MARKET_BASED)
    .reduce((acc, r) => acc + (Number(r.consumption) || 0), 0);
}
